import axios from 'axios';
import { API_ENDPOINT, THEME } from "../Settings";

const api = axios.create({
    baseURL: API_ENDPOINT + "/api/v1",
});

export const setProfileIdInHeader = () => {
    const selectedProfile = localStorage.getItem('selectedProfileId');
    if (selectedProfile && selectedProfile !== 'undefined') {
        api.defaults.headers['cpd-profile-id'] = selectedProfile;
    }
}

// On boot, the profile in localStorage is only a guess until it has been checked
// against the IDP — it may name a profile that was revoked since the last visit.
// Data hooks fire as soon as they mount, which is well before that check can
// finish, so without a gate the first wave of requests goes out with a profile
// the backend will reject. Hold them until App has reconciled.
let releaseProfileGate: () => void;
const profileGate: Promise<void> = new Promise((resolve) => { releaseProfileGate = resolve; });
export const openProfileGate = () => releaseProfileGate();
// Never let the gate strand the app: public share routes never reconcile, and a
// wedged IDP must not mean no requests at all.
setTimeout(() => releaseProfileGate(), 5000);

// Called when the backend rejects our cpd-profile-id as one the user no longer
// has. App wires this up to re-reconcile against the IDP.
let onProfileRejected: (() => void | Promise<void>) | null = null;
export const setOnProfileRejected = (handler: () => void | Promise<void>) => {
    onProfileRejected = handler;
}

// Add an interceptor to set the authentication header
api.interceptors.request.use(async (config) => {
    // Read the profile only once it has been reconciled against the IDP, so we
    // never send one the user has since lost.
    await profileGate;

    // if selectedProfile in local storage, set a header with the selected profile
    const selectedProfile = localStorage.getItem('selectedProfileId');
    if (selectedProfile && selectedProfile !== 'undefined') {
        config.headers['cpd-profile-id'] = selectedProfile;
    }

    const idp_token_json = localStorage.getItem('drx-identity-token')
    let token = null
    let origToken = null
    if (idp_token_json) {
        const parsedToken = JSON.parse(idp_token_json)
        token = parsedToken.access_token
        origToken = parsedToken.original_token
    }
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (origToken) {
        config.headers['x-authorization-orig'] = `Bearer ${origToken}`;
    }

    config.headers['cpd-tenant-id'] = THEME;
    return config;
});

// A profile can be revoked in the Hub while the tab is open, so a correct
// profile at page load is no guarantee it is still correct now. The backend
// answers profile_not_member (as opposed to a plain auth failure) precisely so
// we can recover: refresh the profile list, then replay the request once with
// whatever we reconciled to. Anything else, or a second failure, surfaces to the
// caller as normal.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error.config;
        const problemType = error.response?.data?.type;
        const profileRejected =
            error.response?.status === 403 &&
            typeof problemType === 'string' &&
            problemType.includes('profile_not_member');

        if (profileRejected && config && !config._profileRetry) {
            config._profileRetry = true;
            if (onProfileRejected) {
                await onProfileRejected();
            }
            const reconciled = localStorage.getItem('selectedProfileId');
            if (reconciled && reconciled !== config.headers['cpd-profile-id']) {
                config.headers['cpd-profile-id'] = reconciled;
                return api.request(config);
            }
        }
        return Promise.reject(error);
    }
);

export default api;
